'use client';

import { useRouter } from 'next/navigation';
import { updateProfile } from '@/app/actions/profile';
import { callAction } from '@/lib/call-action';
import { EditableName } from './EditableName';
import { PageLink } from './PageLink';
import { TaglineEditor } from './TaglineEditor';

/**
 * The owner's name, tagline and link, edited on the page. A rename leaves for
 * the new address, since the old one now redirects.
 */
export function OwnerIdentity({
  name,
  username,
  tagline,
  ambassador,
}: {
  name: string;
  username: string;
  tagline: string | null;
  ambassador?: boolean;
}) {
  const router = useRouter();

  const saveName = async (next: string) => {
    const result = await callAction(() => updateProfile({ name: next }, username));
    if (!result.success) return result.error;
    router.refresh();
    return null;
  };

  const saveTagline = async (next: string | null) => {
    const result = await callAction(() => updateProfile({ bio: next ?? '' }, username));
    if (!result.success) return 'Could not save — your line is still here, try again';
    router.refresh();
    return null;
  };

  const saveLink = async (next: string) => {
    const result = await callAction(() => updateProfile({ username: next }, username));
    if (!result.success) return result.error;
    // A full navigation: the page under us is the old address, and a push
    // right after a revalidating action races the action queue.
    // eslint-disable-next-line @next/next/no-location-assign-relative-destination
    window.location.assign(`/${next}`);
    return null;
  };

  return (
    <div className="flex flex-col gap-3">
      <EditableName name={name} ambassador={ambassador} onSave={saveName} />
      <TaglineEditor tagline={tagline} onSave={saveTagline} />
      <PageLink username={username} own={username} onSave={saveLink} />
    </div>
  );
}
